import { timeOpts } from '../lib/timePrefs.js';
import { useEffect, useRef, useState } from 'react';
import type { PaymentConnectionView, PaymentConnections } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';

/**
 * ehr#331 (Taras 10-10) - Data & settings > Payments, laid out like Email & calendar's connections.
 * Stripe via Connect: Connect with Stripe creates the practice's own Stripe account and opens
 * Stripe's hosted onboarding, which comes back to /?settings=payments&stripe=return (status read
 * back from Stripe) or &stripe=refresh (the link expired: continue setup). Admin / manager only.
 * Client health details are never sent to Stripe.
 */

const msgOf = (e: unknown) =>
  e instanceof ApiError ? e.message : 'Something went wrong. Try again.';
const fmtWhen = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...timeOpts(), // ehr#407: the person's 12/24-hour setting
      })
    : '';

function statusPill(s: PaymentConnectionView | null) {
  if (!s) return { cls: 'off', text: 'Not connected' };
  if (s.status === 'active') return { cls: 'on', text: 'Connected' };
  if (s.status === 'restricted') return { cls: 'err', text: 'Needs attention' };
  return { cls: 'wait', text: 'Setup not finished' };
}

/** The `stripe` param Stripe's onboarding returns with, read once and removed from the address. */
function takeStripeParam(): string {
  const url = new URL(window.location.href);
  const v = url.searchParams.get('stripe') ?? '';
  if (v) {
    url.searchParams.delete('stripe');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  return v;
}

export function PaymentsSettings() {
  const [data, setData] = useState<PaymentConnections | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState<'' | 'connect' | 'refresh' | 'disconnect'>('');
  const [msg, setMsg] = useState('');
  const [confirmOff, setConfirmOff] = useState(false);
  // where Stripe sent the browser back from, read once (and taken out of the address)
  const returned = useRef<string | null>(null);
  if (returned.current === null) returned.current = takeStripeParam();

  useEffect(() => {
    api
      .get<PaymentConnections>('/payment-connections')
      .then((d) => {
        setData(d);
        const back = returned.current;
        returned.current = '';
        if (back === 'return' && d.configured && d.stripe) refresh(true);
        if (back === 'refresh' && d.stripe)
          setMsg('The Stripe setup link expired. Continue setup to pick up where you left off.');
      })
      .catch((e: unknown) =>
        setErr(
          e instanceof ApiError && e.status === 403
            ? 'Only admins and managers manage payments.'
            : msgOf(e),
        ),
      );
  }, []);

  const connect = () => {
    if (busy) return;
    setBusy('connect');
    setMsg('');
    api
      .post<{ url: string }>('/payment-connections/stripe/onboard')
      .then((r) => window.location.assign(r.url))
      .catch((e: unknown) => {
        setMsg(msgOf(e));
        setBusy('');
      });
  };
  function refresh(afterReturn = false) {
    setBusy('refresh');
    setMsg('');
    api
      .post<PaymentConnections>('/payment-connections/stripe/refresh')
      .then((d) => {
        setData(d);
        if (afterReturn)
          setMsg(
            d.stripe?.status === 'active'
              ? 'Stripe is connected. You can take payments.'
              : 'Back from Stripe. Setup is not finished yet.',
          );
      })
      .catch((e: unknown) => setMsg(msgOf(e)))
      .finally(() => setBusy(''));
  }
  const disconnect = () => {
    setBusy('disconnect');
    setMsg('');
    api
      .del<PaymentConnections>('/payment-connections/stripe')
      .then((d) => {
        setData(d);
        setConfirmOff(false);
        setMsg('Disconnected. The Stripe account itself is still yours.');
      })
      .catch((e: unknown) => setMsg(msgOf(e)))
      .finally(() => setBusy(''));
  };

  if (err) return <p className="note warn">{err}</p>;
  if (!data) return <div className="empty">Loading…</div>;

  const s = data.stripe;
  const pill = statusPill(s);
  return (
    <div className="payments">
      {!data.configured && (
        <p className="note warn pay-note">
          Payments aren&apos;t switched on on the server yet. Credify support adds the Stripe
          platform key; then you can connect your practice&apos;s Stripe account here.
        </p>
      )}
      <div className="prov">
        <div className="prov-head">
          <span className="prov-mark pay-mark">S</span>
          <div>
            <div className="nm">Stripe</div>
            <div className="sb">
              Card and bank payments, paid out to your practice&apos;s own account
            </div>
          </div>
          <div className="right">
            <span className={`status ${pill.cls}`}>
              <i />
              {pill.text}
            </span>
          </div>
        </div>
        <div className="prov-body">
          {!s ? (
            <p className="pay-copy">
              Connect with Stripe to take client payments. Stripe sets up a Stripe account for your
              practice in a few minutes (business details and a bank account for payouts). Payouts,
              refunds and disputes are handled in that account's own Stripe dashboard. Client health
              details are never sent to Stripe.
            </p>
          ) : (
            <dl className="pay-facts">
              <div>
                <dt>Stripe account</dt>
                <dd>{s.accountId}</dd>
              </div>
              <div>
                <dt>Take payments</dt>
                <dd className={s.chargesEnabled ? 'yes' : 'no'}>
                  {s.chargesEnabled ? 'Yes' : 'Not yet'}
                </dd>
              </div>
              <div>
                <dt>Payouts</dt>
                <dd className={s.payoutsEnabled ? 'yes' : 'no'}>
                  {s.payoutsEnabled ? 'Yes' : 'Not yet'}
                </dd>
              </div>
              <div>
                <dt>Stripe still needs</dt>
                <dd className={s.requirementsDue ? 'no' : 'yes'}>
                  {s.requirementsDue
                    ? `${s.requirementsDue} item${s.requirementsDue === 1 ? '' : 's'}`
                    : 'Nothing'}
                </dd>
              </div>
              {s.checkedAt && (
                <div>
                  <dt>Last checked</dt>
                  <dd>{fmtWhen(s.checkedAt)}</dd>
                </div>
              )}
            </dl>
          )}
          {msg && <p className="pay-msg">{msg}</p>}
          {confirmOff ? (
            <div className="pay-confirm">
              <span>
                Stop using this Stripe account here? The account stays yours in Stripe. Connecting
                again later sets up a new one.
              </span>
              <button className="btn" onClick={() => setConfirmOff(false)} disabled={!!busy}>
                Keep it
              </button>
              <button className="btn danger" onClick={disconnect} disabled={!!busy}>
                {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          ) : (
            <div className="btn-row">
              {!s && (
                <button
                  className="btn primary"
                  onClick={connect}
                  disabled={!data.configured || !!busy}
                >
                  {busy === 'connect' ? 'Opening Stripe…' : 'Connect with Stripe'}
                </button>
              )}
              {s && s.status !== 'active' && (
                <button
                  className="btn primary"
                  onClick={connect}
                  disabled={!data.configured || !!busy}
                >
                  {busy === 'connect' ? 'Opening Stripe…' : 'Continue setup on Stripe'}
                </button>
              )}
              {s && s.status === 'active' && (
                <a
                  className="btn"
                  href="https://dashboard.stripe.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Stripe dashboard
                </a>
              )}
              {s && (
                <button
                  className="btn"
                  onClick={() => refresh()}
                  disabled={!data.configured || !!busy}
                >
                  {busy === 'refresh' ? 'Checking…' : 'Refresh status'}
                </button>
              )}
              {s && (
                <button className="btn ghost" onClick={() => setConfirmOff(true)} disabled={!!busy}>
                  Disconnect
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="foot-note">
        Stripe is the first payment processor. Others can be connected here the same way later.
      </p>
    </div>
  );
}
